import { useCallback, useEffect, useState } from 'react';

import { noopFunction } from '@vestico/utils';

export function detectFrameRate(): Promise<number> {
  return new Promise((resolve) => {
    const numFrames = 30;
    const startTime = performance.now();
    let i = 0;
    const tick = () => {
      if (i < numFrames) {
        i++;
        requestAnimationFrame(tick);
        return;
      }
      const frameRate = numFrames / ((performance.now() - startTime) / 1000);
      resolve(frameRate);
    };
    requestAnimationFrame(() => {
      tick();
    });
  });
}

/**
 * detect iOS/iPad/macOS Low Power Mode, Chromium Energy Saver Mode, and maybe future Firefox power saving mode
 * @async
 * @method detectPowerSavingMode
 * @returns {boolean|undefined} return `undefined` if not sure
 */
export const detectLowPowerMode = async (): Promise<boolean> => {
  // for iOS/iPadOS Safari, and maybe MacBook macOS Safari (not tested)
  if (/(iP(?:hone|ad|od)|Mac OS X)/.test(navigator.userAgent)) {
    // In Low Power Mode, cumulative delay effect happens on setInterval()
    return new Promise((resolve) => {
      const fps = 60;
      const interval = 1000 / fps;
      const numFrames = 30;
      const startTime = performance.now();
      let i = 0;
      const handle = setInterval(() => {
        if (i < numFrames) {
          i++;
          return;
        }
        clearInterval(handle);
        const actualInterval = (performance.now() - startTime) / numFrames;
        const ratio = actualInterval / interval; // 1.3x or more in Low Power Mode, 1.1x otherwise
        resolve(ratio > 1.3);
      }, interval);
    });
  }
  // for Safari, Chromium, and maybe future Firefox
  const frameRate: number | undefined = await detectFrameRate().catch(() => undefined);
  return frameRate !== undefined && frameRate > 0 && frameRate < 29;
};

export const useLowPowerMode = () => {
  const [isLowPowerMode, setIsLowPowerMode] = useState(true);

  const updateLowPowerMode = useCallback(async () => {
    if (isLowPowerMode && !document.hidden) {
      const newLowPowerMode = await detectLowPowerMode();
      setIsLowPowerMode(newLowPowerMode);
    }
  }, [isLowPowerMode]);

  const visibilityListener = useCallback(() => {
    updateLowPowerMode();
  }, [updateLowPowerMode]);

  useEffect(() => {
    document.addEventListener('visibilitychange', visibilityListener, false);
    return () => document.removeEventListener('visibilitychange', visibilityListener);
  }, [visibilityListener]);

  useEffect(() => {
    if (!isLowPowerMode) {
      return noopFunction;
    }

    updateLowPowerMode();

    const intervalID = setInterval(updateLowPowerMode, 6000);
    return () => clearInterval(intervalID);
  }, [isLowPowerMode, updateLowPowerMode]);

  return isLowPowerMode;
};
